import { lazy, Suspense } from "react";
import { Route, Routes, useNavigate } from "react-router-dom-v5-compat";
import { MODAL_ROUTES } from "routing/routes";

import loaderWithRetry from "seneca-common/utils/loaderWithRetry";

const ProgressReportModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "progress-report-modal-chunk" */
        "features/teachers/pages/reports/progress-report-modal/components/ProgressReportModal"
      )
  )
);

const ShareAssignmentModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "share-assignment-chunk" */
        "features/teachers/pages/assignment/modals/AssignmentShareModal"
      )
  )
);

const ManageStudentModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "manage-student-chunk" */
        "features/teachers/common/components/ManageStudentModal/ManageStudentModal"
      )
  )
);

const AssignmentStudentResultModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "assignment-student-result-chunk" */
        "features/teachers/pages/assignment/modals/AssignmentStudentResultModal"
      )
  )
);

const DeleteClassAssignmentModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "delete-assignment-chunk" */
        "features/teachers/pages/assignment/modals/DeleteClassAssignment/DeleteClassAssignmentModal"
      )
  )
);

const DeleteClassAssignmentsModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "delete-assignments-chunk" */
        "features/teachers/pages/assignment/modals/DeleteClassAssignments/DeleteClassAssignmentsModal"
      )
  )
);

const EditMultiClassAssignmentModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "edit-multi-class-assignment-chunk" */
        "features/teachers/pages/assignment/modals/EditMultiClassAssignment/EditMultiClassAssignment"
      )
  )
);

const PreSpeedyMarkingModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "pre-speedy-marking-chunk" */
        "features/teachers/pages/assignment/modals/PreSpeedyMarkingModal/PreSpeedyMarkingModal"
      )
  )
);

const MarkingModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "marking-chunk" */
        "features/teachers/pages/assignment/modals/MarkingModal/MarkingModal"
      )
  )
);

const SectionSelectorModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "section-selector-chunk" */
        "features/teachers/features/insights/components/SectionSelectorModal"
      )
  )
);

const MISClassTriggerModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "mis-class-trigger-chunk" */
        "features/teachers/pages/classes-page/modals/MISClassTrigger/MISClassTriggerModal"
      )
  )
);

const RemoveCourseModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "remove-course-chunk" */
        "features/teachers/pages/class-page/common/modals/RemoveCourseModal/RemoveCourseModal"
      )
  )
);

const RegionSelectionModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "region-selection-chunk" */
        "features/regions/common/components/RegionSelectionModal"
      )
  )
);

const InviteParentInfoModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "invite-parent-info-chunk" */
        "features/teachers/common/components/InviteParentInfoModal"
      )
  )
);

const InviteClassesParentsModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "invite-classes-parents-chunk" */
        "features/teachers/common/components/InviteClassesParentsModal"
      )
  )
);

const ChangeEmailModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "change-email-chunk" */
        "features/settings/features/personal-information/features/change-email/ChangeEmailModal"
      )
  )
);

const DeleteClassModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "delete-class-chunk" */
        "features/teachers/pages/class-settings/modals/DeleteClassModal"
      )
  )
);

const LeaveClassModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "leave-class-chunk" */
        "features/teachers/pages/class-settings/modals/LeaveClassModal"
      )
  )
);

const ArchiveClassesModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "archive-classes-chunk" */
        "features/teachers/common/modals/Archive/ArchiveClassesModal"
      )
  )
);

const HideClassesModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "hide-classes-chunk" */
        "features/teachers/common/modals/HideClasses/HideClassesModal"
      )
  )
);

const UnhideClassesModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "unhide-classes-chunk" */
        "features/teachers/common/modals/UnhideClasses/UnhideClassesModal"
      )
  )
);

const JoinClassModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "join-class-chunk" */
        "features/dashboard/pages/classes/components/JoinClassModal"
      )
  )
);

const InviteTeacherModal = lazy(() =>
  // @ts-ignore
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "invite-teacher-chunk" */
        "features/dashboard/pages/classes/components/InviteTeacherModal"
      )
  )
);

const ClassDoesNotExistModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "class-does-not-exist-chunk" */
        "features/teachers/pages/classes-page/modals/ClassDoesNotExist"
      )
  )
);

const ManageTeachersModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "manage-teachers-chunk" */
        "features/teachers/features/class-sharing/components/ManageTeachersModal"
      )
  )
);

const TransferOwnershipModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "transfer-ownership-chunk" */
        "features/teachers/features/class-sharing/components/TransferOwnershipModal"
      )
  )
);

const RemoveTeacherModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "remove-teacher-chunk" */
        "features/teachers/features/class-sharing/components/RemoveTeacherModal"
      )
  )
);

const ResetPasswordsSchoolModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "reset-passwords-school-chunk" */
        "features/teachers/pages/school/components/ResetPasswordsModal"
      )
  )
);

const JoinSchoolClassModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "join-school-class-chunk" */
        "features/teachers/pages/classes-page/modals/JoinClass/JoinClassModal"
      )
  )
);

const JoinSchoolClassesModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "join-school-classes-chunk" */
        "features/teachers/pages/classes-page/modals/JoinClassesModal/JoinClassesModal"
      )
  )
);

const ClassStudentInviteSuccessModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "class-student-invite-success-chunk" */
        "features/teachers/modals/parents-invite/invite-all-school-parents/modals/SuccessModal"
      )
  )
);

const SchoolRenewalRequestSuccessModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "school-renewal-success-chunk" */
        "features/teachers/features/gating/components/TeacherRenewalRequestSuccessModal"
      )
  )
);

export function ModalRoutes() {
  const navigate = useNavigate();
  return (
    <Routes>
      <Route
        path={MODAL_ROUTES.SHARE_ASSIGNMENT.path}
        element={
          <Suspense>
            <ShareAssignmentModal />
          </Suspense>
        }
      />
      <Route
        path={MODAL_ROUTES.MANAGE_STUDENT.path}
        element={
          <Suspense>
            <ManageStudentModal />
          </Suspense>
        }
      />
      <Route
        path={MODAL_ROUTES.ASSIGNMENT_STUDENT_RESULT.path}
        element={
          <Suspense>
            <AssignmentStudentResultModal />
          </Suspense>
        }
      />
      <Route
        path={MODAL_ROUTES.DELETE_CLASS_ASSIGNMENT.path}
        element={
          <Suspense>
            <DeleteClassAssignmentModal />
          </Suspense>
        }
      />
      <Route
        path={MODAL_ROUTES.DELETE_CLASS_ASSIGNMENTS.path}
        element={
          <Suspense>
            <DeleteClassAssignmentsModal />
          </Suspense>
        }
      />
      <Route
        path={MODAL_ROUTES.EDIT_MULTI_CLASS_ASSIGNMENT.path}
        element={
          <Suspense>
            <EditMultiClassAssignmentModal />
          </Suspense>
        }
      />
      <Route
        path={MODAL_ROUTES.START_SPEEDY_MARKING.path}
        element={
          <Suspense>
            <PreSpeedyMarkingModal />
          </Suspense>
        }
      />
      <Route
        path={MODAL_ROUTES.MARKING.path}
        element={
          <Suspense>
            <MarkingModal />
          </Suspense>
        }
      />
      <Route
        path={MODAL_ROUTES.INSIGHTS_SECTIONS_SELECTOR.path}
        element={
          <Suspense>
            <SectionSelectorModal />
          </Suspense>
        }
      />
      <Route
        path={MODAL_ROUTES.MIS_CLASS_TRIGGER.path}
        element={
          <Suspense>
            <MISClassTriggerModal />
          </Suspense>
        }
      />
      <Route
        path={MODAL_ROUTES.REMOVE_COURSE.path}
        element={
          <Suspense>
            <RemoveCourseModal />
          </Suspense>
        }
      />
      <Route
        path={MODAL_ROUTES.CHOOSE_REGION.path}
        element={
          <Suspense>
            <RegionSelectionModal />
          </Suspense>
        }
      />
      <Route
        path={MODAL_ROUTES.INVITE_PARENT_INFO.path}
        element={
          <Suspense>
            <InviteParentInfoModal />
          </Suspense>
        }
      />
      <Route
        path={MODAL_ROUTES.INVITE_CLASSES_PARENTS.path}
        element={
          <Suspense>
            <InviteClassesParentsModal />
          </Suspense>
        }
      />
      <Route
        path={MODAL_ROUTES.CHANGE_EMAIL.path}
        element={
          <Suspense>
            <ChangeEmailModal />
          </Suspense>
        }
      />
      <Route
        path={MODAL_ROUTES.DELETE_CLASS.path}
        element={
          <Suspense>
            <DeleteClassModal />
          </Suspense>
        }
      />
      <Route
        path={MODAL_ROUTES.LEAVE_CLASS.path}
        element={
          <Suspense>
            <LeaveClassModal />
          </Suspense>
        }
      />
      <Route
        path={MODAL_ROUTES.ARCHIVE_CLASSES.path}
        element={
          <Suspense>
            <ArchiveClassesModal />
          </Suspense>
        }
      />
      <Route
        path={MODAL_ROUTES.HIDE_CLASSES.path}
        element={
          <Suspense>
            <HideClassesModal />
          </Suspense>
        }
      />
      <Route
        path={MODAL_ROUTES.UNHIDE_CLASSES.path}
        element={
          <Suspense>
            <UnhideClassesModal />
          </Suspense>
        }
      />
      <Route
        path={MODAL_ROUTES.PROGRESS_REPORT.path}
        element={
          <Suspense>
            <ProgressReportModal />
          </Suspense>
        }
      />
      <Route
        path={MODAL_ROUTES.JOIN_CLASS_STUDENT.path}
        element={
          <Suspense>
            <JoinClassModal navigate={navigate} />
          </Suspense>
        }
      />
      <Route
        path={MODAL_ROUTES.INVITE_TEACHER_STUDENT.path}
        element={
          <Suspense>
            <InviteTeacherModal />
          </Suspense>
        }
      />
      <Route
        path={MODAL_ROUTES.CLASS_DOES_NOT_EXIST.path}
        element={
          <Suspense>
            <ClassDoesNotExistModal />
          </Suspense>
        }
      />
      <Route
        path={MODAL_ROUTES.MANAGE_TEACHERS.path}
        element={
          <Suspense>
            <ManageTeachersModal />
          </Suspense>
        }
      />
      <Route
        path={MODAL_ROUTES.TRANSFER_OWNERSHIP.path}
        element={
          <Suspense>
            <TransferOwnershipModal />
          </Suspense>
        }
      />
      <Route
        path={MODAL_ROUTES.REMOVE_TEACHER.path}
        element={
          <Suspense>
            <RemoveTeacherModal />
          </Suspense>
        }
      />
      <Route
        path={MODAL_ROUTES.RESET_PASSWORDS_SCHOOL.path}
        element={
          <Suspense>
            <ResetPasswordsSchoolModal />
          </Suspense>
        }
      />
      <Route
        path={MODAL_ROUTES.JOIN_SCHOOL_CLASS.path}
        element={
          <Suspense>
            <JoinSchoolClassModal />
          </Suspense>
        }
      />
      <Route
        path={MODAL_ROUTES.JOIN_SCHOOL_CLASSES.path}
        element={
          <Suspense>
            <JoinSchoolClassesModal />
          </Suspense>
        }
      />
      <Route
        path={MODAL_ROUTES.CLASS_STUDENT_INVITE_SUCCESS.path}
        element={<ClassStudentInviteSuccessModal />}
      />
      <Route
        path={MODAL_ROUTES.SCHOOL_RENEWAL_FORM_SUCCESS.path}
        element={<SchoolRenewalRequestSuccessModal />}
      />
    </Routes>
  );
}
